import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { User, Mail, Heart, Settings as SettingsIcon, Trash2, AlertTriangle, Share2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SharePatientDialog from '@/components/SharePatientDialog';
import ManageAccessSection from '@/components/ManageAccessSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = ({ user, onLogout, setUser }) => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState({
    name: user.name,
    email: user.email,
  });
  const [editing, setEditing] = useState(false);
  const [patients, setPatients] = useState([]);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [selectedPatientToReset, setSelectedPatientToReset] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [selectedPatientToShare, setSelectedPatientToShare] = useState(null);
  const [selectedPatientForAccess, setSelectedPatientForAccess] = useState(null);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/patients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/users/profile`,
        profileData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local storage and state
      const updatedUser = response.data;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      setEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Email already in use')) {
        toast.error('This email is already in use');
      } else {
        toast.error('Failed to update profile');
      }
    }
  };


  const handleResetPatientData = (patient) => {
    setSelectedPatientToReset(patient);
    setShowResetDialog(true);
    setConfirmText('');
  };

  const confirmResetData = async () => {
    if (confirmText !== 'RESET') {
      toast.error('Please type RESET to confirm');
      return;
    }

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/patients/${selectedPatientToReset.id}/data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`All data for ${selectedPatientToReset.name} has been reset`);
      setShowResetDialog(false);
      setSelectedPatientToReset(null);
      setConfirmText('');
    } catch (error) {
      toast.error('Failed to reset patient data');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Account deleted successfully');
      
      // Clear local storage and logout
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Redirect to login
      setTimeout(() => {
        onLogout();
        navigate('/login');
      }, 1000);
      
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account');
    }
  };

  return (
    <div className="min-h-screen pb-24 md:pb-8" data-testid="profile-page">
      <Navigation active="profile" />

      <div className="max-w-4xl mx-auto p-4 md:p-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold" style={{ color: '#333' }}>Profile</h1>
          <p className="text-gray-600 mt-1">Manage your account settings</p>
        </motion.div>

        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card mb-6"
          data-testid="profile-card"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold" style={{ color: '#A2DAD3' }}>Personal Information</h2>
            <button
              data-testid="edit-profile-button"
              onClick={() => (editing ? handleSave() : setEditing(true))}
              className="btn btn-primary text-sm px-4 py-2"
            >
              {editing ? 'Save' : 'Edit'}
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#A2DAD3' }}
              >
                <User size={32} color="white" />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                {editing ? (
                  <input
                    data-testid="name-input"
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  />
                ) : (
                  <p className="text-lg font-medium">{profileData.name}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Mail size={20} style={{ color: '#A2DAD3' }} />
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                {editing ? (
                  <input
                    data-testid="email-input"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    className="input"
                  />
                ) : (
                  <p className="text-lg">{profileData.email}</p>
                )}
                {!editing && (
                  <p className="text-xs text-gray-500 mt-1">Click Edit to change email</p>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Settings Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-6"
          data-testid="settings-card"
        >
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#A2DAD3' }}>App Settings</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: '#f0f9f8' }}>
              <div className="flex items-center gap-3">
                <SettingsIcon size={20} style={{ color: '#A2DAD3' }} />
                <div>
                  <p className="font-medium">Notifications & Reminders</p>
                  <p className="text-sm text-gray-600">Manage alerts and reminders</p>
                </div>
              </div>
              <button
                data-testid="go-to-settings-button"
                onClick={() => navigate('/settings')}
                className="btn btn-primary text-sm px-4 py-2"
              >
                Manage
              </button>
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: '#f0f9f8' }}>
              <div>
                <p className="font-medium">Temperature Unit</p>
                <p className="text-sm text-gray-600">Celsius only</p>
              </div>
              <span className="text-sm font-medium" style={{ color: '#A2DAD3' }}>°C</span>
            </div>

            {/* Delete Account Button */}
            <div 
              className="flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer hover:opacity-80 transition-opacity" 
              style={{ backgroundColor: '#fff5f5', borderColor: '#F9A7A7' }}
              onClick={() => setShowDeleteAccountDialog(true)}
            >
              <div className="flex items-center gap-3">
                <AlertTriangle size={20} style={{ color: '#F9A7A7' }} />
                <div>
                  <p className="font-medium text-red-600">Delete Account</p>
                  <p className="text-sm text-gray-600">Permanently delete your account and all data</p>
                </div>
              </div>
              <button
                className="btn text-sm px-4 py-2"
                style={{ backgroundColor: '#F9A7A7', color: 'white' }}
              >
                Delete
              </button>
            </div>
          </div>
        </motion.div>

        {/* Patient Sharing Section */}
        {patients.filter(p => !p.is_shared).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="card mb-6"
            data-testid="share-patient-card"
          >
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Share2 size={20} style={{ color: '#A2DAD3' }} />
              Share Patient Profiles
            </h2>
            
            <p className="text-sm text-gray-600 mb-4">
              Share patient health data with family members or caregivers. You can customize what data they can access and revoke access anytime.
            </p>

            <div className="space-y-3">
              {patients.filter(p => !p.is_shared).map((patient) => (
                <div key={patient.id}>
                  <div
                    className="flex items-center justify-between p-3 rounded-lg"
                    style={{ backgroundColor: '#f0f9f8', border: '1px solid #A2DAD3' }}
                  >
                    <div className="flex-1">
                      <p className="font-medium">{patient.name}</p>
                      <p className="text-sm text-gray-600">{patient.relationship}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedPatientForAccess(patient);
                        }}
                        className="btn text-sm px-3 py-2"
                        style={{ backgroundColor: 'white', color: '#A2DAD3', border: '1px solid #A2DAD3' }}
                      >
                        Manage Access
                      </button>
                      <button
                        onClick={() => {
                          setSelectedPatientToShare(patient);
                          setShowShareDialog(true);
                        }}
                        className="btn btn-primary text-sm px-4 py-2 flex items-center gap-2"
                      >
                        <Share2 size={14} />
                        Share
                      </button>
                    </div>
                  </div>
                  
                  {/* Show Manage Access section when selected */}
                  {selectedPatientForAccess?.id === patient.id && (
                    <div className="mt-3">
                      <ManageAccessSection patient={patient} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Reset Patient Data Card */}
        {patients.filter(p => !p.is_shared).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card mb-6"
            data-testid="reset-data-card"
            style={{ backgroundColor: '#fff5f5', border: '2px solid #F9A7A7' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={20} style={{ color: '#F9A7A7' }} />
              <h2 className="text-xl font-semibold" style={{ color: '#F9A7A7' }}>Reset Patient Data</h2>
            </div>
            
            <p className="text-sm text-gray-700 mb-4">
              This will permanently delete all health data for a patient including symptoms, medications, appointments, blood pressure, hydration, and notes.
            </p>

            <div className="space-y-3">
              {patients.filter(p => !p.is_shared).map((patient) => (
                <div
                  key={patient.id}
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: 'white', border: '1px solid #F9A7A7' }}
                >
                  <div>
                    <p className="font-medium">{patient.name}</p>
                    <p className="text-sm text-gray-600">{patient.relationship}</p>
                  </div>
                  <button
                    data-testid={`reset-data-${patient.id}`}
                    onClick={() => handleResetPatientData(patient)}
                    className="btn text-sm px-4 py-2 flex items-center gap-2"
                    style={{ backgroundColor: '#F9A7A7', color: 'white' }}
                  >
                    <Trash2 size={14} />
                    Reset Data
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}


        {/* Logout Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <button
            data-testid="logout-button"
            onClick={onLogout}
            className="w-full p-4 rounded-xl font-medium"
            style={{ backgroundColor: '#fff5f5', color: '#F9A7A7', border: '2px solid #F9A7A7' }}
          >
            Sign Out
          </button>
        </motion.div>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <div className="flex items-center justify-center gap-2">
            Made with <Heart size={16} style={{ color: '#F9A7A7' }} fill="#F9A7A7" /> for caregivers
          </div>
        </div>


      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#F9A7A7' }}>
              <AlertTriangle size={24} />
              Reset Patient Data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="reset-confirmation-dialog">
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#fff5f5', border: '2px solid #F9A7A7' }}>
              <p className="font-semibold mb-2">⚠️ Warning: This action cannot be undone!</p>
              <p className="text-sm text-gray-700">
                All health data for <strong>{selectedPatientToReset?.name}</strong> will be permanently deleted:
              </p>
              <ul className="text-sm text-gray-700 mt-2 list-disc list-inside space-y-1">
                <li>Symptoms and symptom history</li>
                <li>Medications and medication logs</li>
                <li>Appointments</li>
                <li>Blood pressure records</li>
                <li>Hydration tracking</li>
                <li>Notes</li>
                <li>Goals</li>
              </ul>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Type <strong>RESET</strong> to confirm:
              </label>
              <input
                data-testid="reset-confirm-input"
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type RESET"
                className="w-full"
                autoComplete="off"
              />
            </div>

            <div className="flex gap-3">
              <button
                data-testid="cancel-reset-button"
                onClick={() => {
                  setShowResetDialog(false);
                  setConfirmText('');
                  setSelectedPatientToReset(null);
                }}
                className="btn flex-1"
                style={{ backgroundColor: '#e5e5e5', color: '#333' }}
              >
                Cancel
              </button>
              <button
                data-testid="confirm-reset-button"
                onClick={confirmResetData}
                disabled={confirmText !== 'RESET'}
                className="btn flex-1"
                style={{ 
                  backgroundColor: confirmText === 'RESET' ? '#F9A7A7' : '#ccc',
                  color: 'white',
                  opacity: confirmText === 'RESET' ? 1 : 0.6,
                  cursor: confirmText === 'RESET' ? 'pointer' : 'not-allowed'
                }}
              >
                Reset All Data
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Patient Dialog */}
      <SharePatientDialog
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        patient={selectedPatientToShare}
      />

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteAccountDialog} onOpenChange={setShowDeleteAccountDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={24} />
              Delete Account
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#fff5f5', border: '2px solid #F9A7A7' }}>
              <p className="font-semibold text-red-600 mb-2">⚠️ Warning: This action cannot be undone!</p>
              <p className="text-sm text-gray-700">
                Deleting your account will permanently remove:
              </p>
              <ul className="text-sm text-gray-700 mt-2 space-y-1 list-disc list-inside">
                <li>All patient profiles</li>
                <li>All health records (symptoms, medications, appointments)</li>
                <li>All blood pressure and blood sugar data</li>
                <li>All notes and critical events</li>
                <li>Your account information</li>
              </ul>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Type <span className="font-bold text-red-600">DELETE</span> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="w-full px-4 py-2 border-2 rounded-lg"
                style={{ borderColor: deleteConfirmText === 'DELETE' ? '#F9A7A7' : '#e5e5e5' }}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteAccountDialog(false);
                  setDeleteConfirmText('');
                }}
                className="btn flex-1"
                style={{ backgroundColor: '#e5e5e5', color: '#333' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE'}
                className="btn flex-1"
                style={{ 
                  backgroundColor: deleteConfirmText === 'DELETE' ? '#F9A7A7' : '#ccc',
                  color: 'white',
                  opacity: deleteConfirmText === 'DELETE' ? 1 : 0.6,
                  cursor: deleteConfirmText === 'DELETE' ? 'pointer' : 'not-allowed'
                }}
              >
                Delete Account
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      </div>
    </div>
  );
};

export default Profile;
